import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Plus, Search, Heart, Phone, Mail, MapPin, Loader2, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { PhoneWithWhatsApp } from '../components/WhatsAppLink';

const TIPOLOGIE = [
  { value: 'supermercato', label: 'Supermercato' },
  { value: 'azienda', label: 'Azienda' },
  { value: 'privato', label: 'Privato' },
  { value: 'associazione', label: 'Associazione' },
];

const DonatoreForm = ({ donatore, onSave, onClose }) => {
  const [formData, setFormData] = useState(donatore || {
    nome_donatore: '', tipologia: 'privato', referente: '',
    email: '', telefono: '', indirizzo: '', note: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const processedData = {
        ...formData,
        referente: formData.referente || null, email: formData.email || null,
        telefono: formData.telefono || null, indirizzo: formData.indirizzo || null,
        note: formData.note || null
      };
      await onSave(processedData);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Nome Donatore *</Label>
        <Input value={formData.nome_donatore} onChange={(e) => setFormData({ ...formData, nome_donatore: e.target.value })} required data-testid="donatore-nome-input" />
      </div>
      <div className="space-y-2">
        <Label>Tipologia *</Label>
        <Select value={formData.tipologia} onValueChange={(v) => setFormData({ ...formData, tipologia: v })}>
          <SelectTrigger data-testid="donatore-tipologia-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TIPOLOGIE.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Referente</Label>
          <Input value={formData.referente} onChange={(e) => setFormData({ ...formData, referente: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Telefono</Label>
          <Input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} type="tel" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Email</Label>
        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label>Indirizzo</Label>
        <Input value={formData.indirizzo} onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label>Note</Label>
        <Textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={2} />
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="donatore-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {donatore ? 'Aggiorna' : 'Crea'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const DonatoreMobileCard = ({ d, isAdmin, openEdit, handleDelete }) => (
  <div className="mobile-card-item" data-testid={`donatore-card-${d.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{d.nome_donatore}</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {TIPOLOGIE.find(t => t.value === d.tipologia)?.label || d.tipologia}
          </Badge>
        </div>
        {d.referente && <p className="text-xs text-muted-foreground mt-0.5">Ref: {d.referente}</p>}
        <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-muted-foreground">
          {d.telefono && (
            <span onClick={(e) => e.stopPropagation()}>
              <PhoneWithWhatsApp phone={d.telefono} message="Buongiorno, vi contattiamo dal banco alimentare." />
            </span>
          )}
          {d.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{d.email}</span>}
        </div>
      </div>
      {isAdmin() && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => openEdit(d)} data-testid={`edit-donatore-${d.id}`}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => handleDelete(d.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  </div>
);

export const DonatoriPage = () => {
  const { API, isAdmin } = useAuth();
  const [donatori, setDonatori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDonatore, setEditingDonatore] = useState(null);

  useEffect(() => { fetchDonatori(); }, []);

  const fetchDonatori = async () => {
    try {
      const response = await axios.get(`${API}/donatori`);
      setDonatori(response.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (editingDonatore) {
      await axios.put(`${API}/donatori/${editingDonatore.id}`, data);
      toast.success('Donatore aggiornato');
    } else {
      await axios.post(`${API}/donatori`, data);
      toast.success('Donatore creato');
    }
    fetchDonatori();
    setEditingDonatore(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo donatore?')) return;
    try {
      await axios.delete(`${API}/donatori/${id}`);
      toast.success('Donatore eliminato');
      fetchDonatori();
    } catch (err) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const openEdit = (d) => { setEditingDonatore(d); setDialogOpen(true); };
  const openCreate = () => { setEditingDonatore(null); setDialogOpen(true); };

  const filteredDonatori = donatori.filter(d =>
    d.nome_donatore.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="donatori-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Donatori</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Gestisci i donatori del banco alimentare</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button onClick={openCreate} className="w-full sm:w-auto" data-testid="add-donatore-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Donatore
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingDonatore ? 'Modifica Donatore' : 'Nuovo Donatore'}</DialogTitle>
              </DialogHeader>
              <DonatoreForm donatore={editingDonatore} onSave={handleSave} onClose={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input placeholder="Cerca donatore..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="donatori-search-input" />
      </div>

      {/* Content */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : filteredDonatori.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <Heart className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">Nessun donatore trovato</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipologia</TableHead>
                      <TableHead>Contatti</TableHead>
                      <TableHead className="hidden lg:table-cell">Referente</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDonatori.map((d) => (
                      <TableRow key={d.id} data-testid={`donatore-row-${d.id}`}>
                        <TableCell>
                          <div className="font-medium">{d.nome_donatore}</div>
                          {d.indirizzo && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="w-3 h-3" />{d.indirizzo}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {TIPOLOGIE.find(t => t.value === d.tipologia)?.label || d.tipologia}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {d.telefono && (
                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="w-3 h-3" />
                                <PhoneWithWhatsApp phone={d.telefono} message="Buongiorno, vi contattiamo dal banco alimentare." />
                              </div>
                            )}
                            {d.email && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Mail className="w-3 h-3" />{d.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{d.referente || '-'}</TableCell>
                        <TableCell className="text-right">
                          {isAdmin() && (
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`edit-donatore-${d.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(d.id)} className="text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card List */}
              <div className="md:hidden">
                {filteredDonatori.map((d) => (
                  <DonatoreMobileCard key={d.id} d={d} isAdmin={isAdmin} openEdit={openEdit} handleDelete={handleDelete} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DonatoriPage;
